import { useQuery } from "@tanstack/react-query";
import type { EstateReadyScore, NextStep, Essential } from "@shared/readiness";

/**
 * Single typed query for the Estate Ready Score payload (incl. `nextSteps`).
 * Both `DualProgressCard` (the meter) and `StartHereCard` (the router) consume
 * this — same query key, so TanStack dedupes the fetch and the type lives once.
 */
export function useEstateReadyScore(clientId: string | undefined) {
  return useQuery<EstateReadyScore>({
    queryKey: ["/api/clients", clientId, "estate-ready-score"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/estate-ready-score`);
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
    enabled: !!clientId,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

/** Thin selector: the server already returns `nextSteps` tier-ordered + unmet-only. */
export function getNextSteps(data: EstateReadyScore | undefined): NextStep[] {
  return data?.nextSteps ?? [];
}

/**
 * The 10 essentials in fixed planner-card order (s87). The denominator is always 10
 * — Guardian + Property are done-when-declined, never N/A — so the server no longer
 * emits an `na` essential; the filter is kept as a harmless guard. Order is preserved
 * exactly as the server emits it.
 */
export function getEssentials(data: EstateReadyScore | undefined): Essential[] {
  return (data?.essentials ?? []).filter(e => e.status !== "na");
}
